import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { OnlyFansLists } from '../types/onlyFansTypes';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { OnlyFansPosts } from './useSubscriberPosts';

export interface KicktimeWastersOnLiveSettings {
    onlyfansId: number;
    tippedHoursAgo: number;
    usersCanWatchMinutes: number;
    usersCanWatchMinutesOnRejoin: number;
    numberOfRejoinsAllowed: number;
    publicShameFan: boolean;
    sendWarningMessages: boolean;
    onlySendFinalWarning: boolean;
    whiteListListId: string;
    minimumTipAmount: number;
}

export interface kickTimeWastersWhiteList {
    onlyfansId: number;
    fanId: number;
    fanName: string;
    fanDisplayName: string;
}

export interface kickTimeWastersBlackList {
    onlyfansId: number;
    fanId: number;
    fanName: string;
    fanDisplayName: string;
}

const useSextLiveStreamForceAutoKick = () => {
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const userContext = useContext(UserContext);

    const getAutoKickSettings = async (): Promise<KicktimeWastersOnLiveSettings> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick`;

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });
        return data;
    };

    const updateAutoKickSettings = async (item: KicktimeWastersOnLiveSettings): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/update`;
        const data = await axios
            .post(query, item)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const updateAutoKickSettingsService = async (status: boolean): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/service/${status}`;
        const data = await axios
            .post(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const selectAutoKickServiceStatus = async () => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/service`;

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });
        return data;
    };

    // Auto Kick settings mode: false = whole-stream (one settings set), true = per-goal.
    const getKickMode = async (): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/mode`;
        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const setKickMode = async (perGoal: boolean): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autoKick/mode/${perGoal}`;
        const data = await axios
            .post(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const kickAllCurrentNonSpenders = async (): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/kickall`;
        const data = await axios
            .post(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const getAutoKickWhiteList = async (): Promise<kickTimeWastersWhiteList[]> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/whitelist`;

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });
        return data;
    };

    const autoKickAddToWhiteList = async (payload: kickTimeWastersWhiteList): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/whitelist/add`;
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const autoKickRemoveFromWhiteList = async (payload: kickTimeWastersWhiteList): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/whitelist/remove`;
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const getAutoKickBlackList = async (): Promise<kickTimeWastersBlackList[]> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/blacklist`;

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });
        return data;
    };

    const autoKickAddToBlackList = async (payload: kickTimeWastersBlackList): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/blacklist/add`;
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const autoKickRemoveFromBlackList = async (payload: kickTimeWastersBlackList): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/autokick/blacklist/remove`;
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const theAutoKickSettings = useQuery(
        ['theAutoKickSettings', params.userId],
        () => {
            return getAutoKickSettings();
        },
        {
            staleTime: 60 * 1000 * 15, // 15 minutes
            refetchOnWindowFocus: false,
            enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
        },
    );

    return {
        ...theAutoKickSettings,
        updateAutoKickSettings,
        updateAutoKickSettingsService,
        selectAutoKickServiceStatus,
        kickAllCurrentNonSpenders,
        getAutoKickWhiteList,
        autoKickAddToWhiteList,
        autoKickRemoveFromWhiteList,
        getAutoKickBlackList,
        autoKickAddToBlackList,
        autoKickRemoveFromBlackList,
        getKickMode,
        setKickMode,
    };
};
export default useSextLiveStreamForceAutoKick;
